import * as React from "react";
import { Box, Button, Grid, Link, Stack, Typography } from "@mui/material";

// app setting imports
import { ChatContext, AuthenticationContext, ImagesContext } from "../Context";
import { getHeaders } from "../utils/HeaderHelper";
import { PrivilegesContext } from "../PrivilegesContext";
import { checkPrivileges } from "../utils/PrivilegesHelper";

// component imports
import FollowUpForm from "../modals/FollowUpForm";
import FlashMessage from "../shared/FlashMessage";
import { useGetPatientInfo } from "../hooks/patients/useGetPatientInfo";
import QuestionnaireQr from "../modals/QuestionnaireQr";
import { useCustomerPermission } from "../hooks/useCustomerPermission";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import { snakeCaseToTitleCase } from "../utils/CaseFormatHelper";
import { capitalize } from "../utils/StringHelper";
import PatientAllergies from "../patient_allergies/PatientAllergies";
import DoNotContactModal from "../modals/DoNotContactModal"; 

const addFollowupIcon = require("../../images/followupAddIcon.svg");
const qrIcon = require("../../images/qrIcon.svg");
const allergyIcon = require("../../images/allergy.svg");
const manageAllergyIcon = require("../../images/manageallergy.svg");
const doNotContactIcon = require("../../images/doNotContactIcon.svg");
const doNotContactIconRed = require("../../images/doNotContactIconRed.svg");
import moment from "moment/moment";
import { useFlash } from "../FlashContext";

interface Props {
  patient_id: string;
  setRefreshActionQueue?: React.Dispatch<React.SetStateAction<string>>;
  refreshPatientInfo: boolean;
  setRefreshPatientInfo: Function;
}
const PatientInfo: React.FC<Props> = (props: any) => {
  // authentication context
  const authenticationSetting = React.useContext(AuthenticationContext);
  const {
    showChatList,
    setShowChatList,
    setChatWindowControllers,
    setActiveChatGroup,
  } = React.useContext(ChatContext);
  const userPrivileges = React.useContext<any>(PrivilegesContext);
  const imagesList = React.useContext(ImagesContext);

  const [assets, setAssets] = React.useState<any>({});
  const [fetchedChannelId, setFetchedChannelId] = React.useState<string>("");
  const [actionModalOpen, setActionModalOpen] = React.useState<boolean>(false);
  const [qrModalOpen, setQrModalOpen] = React.useState<boolean>(false);
  const [allergiesModalOpen, setAllergiesModalOpen] =
    React.useState<boolean>(false);
  const [dncModalOpen, setDncModalOpen] = React.useState<boolean>(false);
  const [currentDncDate, setCurrentDncDate] = React.useState<string | null>(null);
  const [connectedToHIE, setConnectedToHIE] = React.useState<boolean>(false);
  const [hieButtonLabel, setHieButtonLabel] =
    React.useState<string>("Connect to HIE");
  const [hieRetrying, setHieRetrying] = React.useState<boolean>(false);
  
  const { patient, refetch } = useGetPatientInfo(
    props.patient_id,
    authenticationSetting.csrfToken,
  );

  // Sync state when patient data loads
  React.useEffect(() => {
    if (patient) {
      setCurrentDncDate(patient.do_not_contact_until || null);
    }
  }, [patient]);

  // Active = Date exists AND is today or in the future
  const isDncActive =
    currentDncDate &&
    moment(currentDncDate, "YYYY-MM-DD").startOf("day").isSameOrAfter(moment().startOf("day"));

  // --------------------------------------

  const [patientTags, setPatientTags] = React.useState([]);
  const [allergies, setAllergies] = React.useState([]);

  const { showToast } = useFlash();

  const { permitted: tabletPermitted } = useCustomerPermission(
    "Allow questionnaires to display on local device",
  );

  React.useEffect(() => {
    getPatientInfoAssets();
    getChatInfo();
  }, []);

  const getPatientInfoAssets = () => {
    fetch(`/reports/reports_patient_information_assets`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.message);
        } else {
          setAssets(result?.resource);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const getChatInfo = () => {
    fetch(`/data_fetching/chats/get_channel_between/${props.patient_id}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (result.success == false) {
          console.log(result.message);
        } else {
          setFetchedChannelId(result?.resource?.channel_id);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const handleQuestionnaireModelClick = () => {
    setQrModalOpen(true);
  };

  const contactChannel =
    patient?.contact_channel === "mobile_phone"
      ? patient?.mobile_phone_number
      : patient?.contact_channel === "home_phone"
        ? patient?.home_phone_number
        : patient?.contact_channel === "email"
          ? patient?.email
          : "";

  const generateTags = (patient: any) => {
    const tags = [];

    if (patient?.cgm_enabled) {
      tags.push({ label: "CGM", color: "#FF890A" });
    }

    if (patient?.program_titles && patient?.program_titles.length > 0) {
      tags.push({
        label: "Programs",
        color: "#EBB708",
        count: patient.program_titles.length.toString(),
      });
    }

    if (patient?.ltc_facility_names && patient?.ltc_facility_names.length > 0) {
      tags.push({ label: "Living Facility", color: "#07BC2A" });
    }

    if (patient?.patient_insurance) {
      tags.push({
        label: capitalize(patient.patient_insurance.insurance_type),
        color: "#3F51B5",
      });
    }

    if (
      patient?.patient_forecast_immunizations &&
      patient?.patient_forecast_immunizations.length > 0
    ) {
      tags.push({
        label: "Immunizations",
        color: "#F00",
        count: patient.patient_forecast_immunizations.length.toString(),
      });
    }

    if (
      patient?.diagnosis_code_values &&
      patient?.diagnosis_code_values.length > 0
    ) {
      tags.push({
        label: "Diagnosis Codes",
        color: "#AE5C04",
        count: patient.diagnosis_code_values.length.toString(),
      });
    }

    if (patient?.list_status && patient?.list_status !== "Not on List") {
      tags.push({ label: patient.list_status, color: "#800080" });
    }

    setPatientTags(tags);
  };

  React.useEffect(() => {
    patient && generateTags(patient);
  }, [patient]);

  React.useEffect(() => {
    if (props.refreshPatientInfo) {
      refetch();
      props.setRefreshPatientInfo(false);
    }
  }, [props.refreshPatientInfo]);

  const closeAllergiesModal = () => {
    setAllergiesModalOpen(false);
  };

  const openAllergiesModal = () => {
    setAllergiesModalOpen(true);
  };

  const fetchPatientAllergiesCount = React.useCallback(() => {
    fetch(`/allergies?patient_id=${props.patient_id}`, {
      method: "GET",
      headers: getHeaders(authenticationSetting.csrfToken),
    })
      .then((result) => result.json())
      .then((result) => {
        if (!result.success) {
          console.log(result.error);
        } else {
          setAllergies(result.resource.allergies);
        }
      })
      .catch((error) => {
        console.log(error);
      });
  }, [props.patient_id, authenticationSetting.csrfToken]);

  React.useEffect(() => {
    fetchPatientAllergiesCount();
  }, [fetchPatientAllergiesCount]);

  const handleTryLinking = () => {
    setHieButtonLabel("Connecting to HIE...");
    if (props.patient_id) {
      fetch(`/data_fetching/link_with_ndhin/${props.patient_id}`, {
        method: "POST",
        headers: getHeaders(authenticationSetting.csrfToken),
      })
        .then((result) => result.json())
        .then((result) => {
          if (result.success == false) {
            setConnectedToHIE(false);
            showToast({
              title: "Error Connecting to HIE",
              text: result.message,
              type: "error",
            });
            setHieRetrying(false);
            setHieButtonLabel("Connect to HIE");
          } else {
            const msg = (result.message || "").toString().toLowerCase();
            const isConnected =
              msg.includes("linked") || msg.includes("success");
            if (isConnected) {
              setConnectedToHIE(true);
              setHieRetrying(false);
              setHieButtonLabel("Connect to HIE");
              showToast({
                title: "Connected to HIE",
                text: result.message,
                type: "success",
              });
            } else {
              setConnectedToHIE(false);
              setHieRetrying(true);
              setHieButtonLabel("Connecting to HIE...");
              showToast({
                title: "HIE Connection Failed",
                text: result.message || "Cannot link now. We will retry daily.",
                type: "info",
              });
            }
          }
        })
        .catch((error) => {
          console.log(error);
          setHieRetrying(false);
          setHieButtonLabel("Connect to HIE");
        });
    }
  };

  return (
    <Grid container className="patient-info-container">
      <Grid item xs={12} alignItems="center">
        <Stack
          direction={"row"}
          justifyContent="space-between"
          alignItems={"center"}
          px={"25px"}
          py={"15px"}
          minHeight={"50px"}
        >
          <Stack direction={"row"} minWidth={"40%"} spacing={2}>
            <Link
              style={{
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
              }}
              href={`/patients/`}
            >
              <ArrowBackIosNewIcon
                style={{
                  borderRadius: "3px",
                  padding: "8px",
                  color: "black",
                  boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                }}
              />
            </Link>
            <Stack className="info-column" sx={{ rowGap: "4px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  columnGap: "8px",
                }}
              >
                <Typography
                  noWrap
                  style={{
                    fontWeight: "bold",
                    fontSize: "18px",
                  }}
                >
                  {patient?.name}
                </Typography>
                {patient?.gender === "Male" ? (
                  <MaleIcon sx={{ width: "20px", height: "20px" }} />
                ) : patient?.gender === "Female" ? (
                  <FemaleIcon sx={{ width: "20px", height: "20px" }} />
                ) : patient?.gender === "Unknown" ? (
                  <QuestionMarkIcon sx={{ width: "20px", height: "20px" }} />
                ) : null}
                <Typography
                  sx={{
                    fontFamily: "QuicksandLight",
                    fontSize: "15px",
                  }}
                >
                  {patient?.age && patient.age}
                  {patient?.gender && ` • ${patient.gender}`}
                  {patient?.date_of_birth &&
                    ` • DOB: ${moment(patient.date_of_birth).format(
                      "MM/DD/YYYY",
                    )}`}

                  {patient?.full_address && ` • ${patient.full_address}`}
                  {patient?.contact_channel && (
                    <>
                      {" • "}
                      {snakeCaseToTitleCase(
                        patient.contact_channel.split("_")[0],
                      )}
                      {contactChannel && ` ${contactChannel}`}
                    </>
                  )}
                </Typography>
                {connectedToHIE ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      backgroundColor: "#EDF7EE",
                      color: "#07BC2A",
                      fontSize: "10px",
                      fontWeight: "600",
                      lineHeight: "16px",
                      padding: "0px 10px",
                      height: "20px",
                      borderRadius: "100px",
                    }}
                  >
                    Connected to HIE
                  </div>
                ) : (
                  <Button
                    sx={{
                      height: "20px",
                      fontSize: "10px",
                      minWidth: "110px",
                    }}
                    onClick={hieRetrying ? undefined : handleTryLinking}
                    disabled={hieRetrying}
                  >
                    {hieButtonLabel}
                  </Button>
                )}
              </div>
              <Stack
                direction={"row"}
                spacing={2}
                className="data-point"
                style={{ padding: "0px" }}
              >
                <div style={{ display: "flex", alignItems: "center" }}>
                  {patientTags?.length > 0 && (
                    <Box display="flex" alignItems="center">
                      <Typography
                        variant="subtitle2"
                        sx={{
                          marginRight: 2,
                          color: "#757575",
                          fontSize: "13px",
                          fontWeight: "700",
                        }}
                      >
                        Patient Tags:
                      </Typography>
                      <Box display="flex" gap={3}>
                        {patientTags.map((tag, index) => (
                          <Box
                            key={index + 1}
                            sx={{
                              bgcolor: "#EFE9E7",
                              p: "2px 8px",
                              fontSize: "10px",
                              color: "#000",
                              borderLeft: `4px solid ${tag.color}`,
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {tag.label}
                            {tag.count && (
                              <Box
                                sx={{
                                  backgroundColor: "#fff",
                                  borderRadius: "80px",
                                  display: "flex",
                                  alignItems: "center",
                                  width: "23px",
                                  justifyContent: "center",
                                  textAlign: "center",
                                  marginLeft: "5px",
                                  padding: "1px 2px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    marginLeft: 1,
                                    color: "#000",
                                    fontWeight: "bold",
                                    fontSize: "10px",
                                    margin: "0px",
                                  }}
                                >
                                  {tag.count}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                </div>
              </Stack>
            </Stack>
          </Stack>
          <Stack direction={"row"} gap={1} className="info-column">
            
            <Link
              className={"action-icon action-icon-container"}
              onClick={() => setDncModalOpen(true)}
              style={{ height: "100%", margin: "0 1px 0 1px", cursor: "pointer" }}
              underline="none"
            >
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                alignItems="center"
              >
                <img
                  src={isDncActive ? doNotContactIconRed : doNotContactIcon}
                  alt="do-not-contact-icon"
                  className="action-icon-image"
                  style={{ width: "35px", maxHeight: "30px" }}
                />
                <p style={{ maxWidth: "68px", whiteSpace: "pre-wrap" }}>
                  {`Do Not\nContact`}
                </p>
              </Grid>
            </Link>

            <Link
              className={
                tabletPermitted
                  ? "action-icon action-icon-container"
                  : "action-icon-disabled action-icon-container"
              }
              onClick={openAllergiesModal}
              style={{ height: "100%", margin: "0 1px 0 1px" }}
              underline="none"
            >
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                alignItems="center"
              >
                <img
                  src={allergies?.length > 0 ? manageAllergyIcon : allergyIcon}
                  alt="add-followup-icon"
                  className="action-icon-image"
                  style={{ width: "35px", maxHeight: "30px" }}
                />
                <p style={{ maxWidth: "68px", whiteSpace: "pre-wrap" }}>
                  {allergies?.length > 0 ? "Manage Allergies" : `Add\nAllergy`}
                </p>
              </Grid>
            </Link>
            <Link
              className={
                tabletPermitted
                  ? "action-icon action-icon-container"
                  : "action-icon-disabled action-icon-container"
              }
              onClick={
                tabletPermitted ? handleQuestionnaireModelClick : undefined
              }
              style={{ height: "100%", margin: "0 1px 0 1px" }}
              underline="none"
            >
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                alignItems="center"
              >
                <img
                  src={qrIcon}
                  alt="add-followup-icon"
                  className="action-icon-image"
                  style={{ width: "35px", maxHeight: "30px" }}
                />
                <p style={{ maxWidth: "68px" }}>Guided Questionnaire</p>
              </Grid>
            </Link>
            <Link
              className={"action-icon action-icon-container"}
              onClick={() => setActionModalOpen(!actionModalOpen)}
              style={{ height: "100%", margin: "0 1px 0 1px" }}
              underline="none"
            >
              <Grid
                container
                direction="column"
                justifyContent="space-between"
                alignItems="center"
              >
                <img
                  src={addFollowupIcon}
                  alt="add-followup-icon"
                  className="action-icon-image"
                  style={{ width: "35px", maxHeight: "30px" }}
                />
                <p style={{ maxWidth: "68px" }}>Create Quick Action</p>
              </Grid>
            </Link>
          </Stack>
        </Stack>
      </Grid>
      
      <FollowUpForm
        patient_id={props.patient_id}
        modalOpen={actionModalOpen}
        setModalOpen={setActionModalOpen}
        showToast={showToast}
        onSuccess={() => props.setRefreshActionQueue?.()}
      />
      {qrModalOpen && (
        <QuestionnaireQr
          modalOpen={qrModalOpen}
          setModalOpen={setQrModalOpen}
          patientId={props.patient_id}
        />
      )}

      {allergiesModalOpen && (
        <PatientAllergies
          patientId={props.patient_id}
          allergiesModalOpen={allergiesModalOpen}
          closeAllergiesModal={closeAllergiesModal}
          refreshAllergies={fetchPatientAllergiesCount}
        />
      )}

      {dncModalOpen && (
        <DoNotContactModal
          open={dncModalOpen}
          onClose={() => setDncModalOpen(false)}
          patientId={props.patient_id}
          initialDncDate={currentDncDate} 
          onSaveSuccess={(newDate, actionType) => {
            // Instantly updates icon and sets up background sync
            setCurrentDncDate(newDate); 
            refetch(); 

            // Matches Acceptance Criteria for Toast Copy
            let toastText = "Do Not Contact date saved.";
            if (actionType === "updated") toastText = "Do Not Contact date updated.";
            if (actionType === "removed") toastText = "Do Not Contact date removed.";

            showToast({
              title: "Success",
              text: toastText,
              type: "success",
            });
          }}
        />
      )}
    </Grid>
  );
};

export default PatientInfo;